import type {
	GreenhouseQuestion,
	GreenhouseDemographicQuestion,
	LocationQuestions,
	DataCompliance,
	EducationQuestions,
} from 'lib/careers/types'
import { useFormContext } from 'react-hook-form'
import TextInputField from '@hashicorp/react-design-system-components/src/components/form/text-input'
import FileInputField from '@hashicorp/react-design-system-components/src/components/form/file-input'
import SelectField from '@hashicorp/react-design-system-components/src/components/form/select'
import TextAreaField from '@hashicorp/react-design-system-components/src/components/form/textarea'
import CheckboxField from '@hashicorp/react-design-system-components/src/components/form/checkbox'
import { Group as RadioGroup } from '@hashicorp/react-design-system-components/src/components/form/radio'
import {
	COMPLIANCE_FIELD_NAME,
	COMPLIANCE_LABEL,
	MULTI_SELECT_DEFAULT,
} from 'lib/careers/greenhouse-careers'
import { LocationDropdown } from './location'
import {
	DateRangeInputs,
	ClientSideSearchDropdown,
	ServerSideSearchDropdown,
} from './education'

type GreenhouseQuestionFieldProps =
	| {
			questionType: 'standard'
			question: GreenhouseQuestion
	  }
	| {
			questionType: 'demographic'
			question: GreenhouseDemographicQuestion
	  }
	| {
			questionType: 'location'
			question: LocationQuestions[number]
	  }

export const GreenhouseQuestionField = ({
	question,
	questionType,
}: GreenhouseQuestionFieldProps) => {
	/**
	 * Lattitude and longitude should no longer be needed,
	 * once LocationDropdown can gather lat and long
	 * for its selected location
	 */
	if (question.label === 'Latitude' || question.label === 'Longitude') {
		return null
	}

	switch (questionType) {
		case 'standard':
			return <StandardQuestionField question={question} />
		case 'demographic':
			return <DemographicQuestionField question={question} />
		case 'location':
			return <LocationDropdown question={question} />
		default:
			console.error(`Unknown question type: ${questionType}`)
			return null
	}
}

const StandardQuestionField = ({
	question,
}: {
	question: GreenhouseQuestion
}) => {
	const { register, formState } = useFormContext()
	const { label, fields, required, description } = question
	const questionField = fields[0]
	const fieldProp = register(questionField.name, { required })
	const errorMsg = formState.errors[questionField.name]?.message?.toString()
	const descriptionStr = description?.replace(/<\/?[^>]+(>|$)/g, '') // strip html tags

	switch (questionField.type) {
		case 'input_text':
			return (
				<TextInputField
					field={fieldProp}
					label={label}
					helperText={descriptionStr}
					isRequired={required}
					size="large"
					error={errorMsg}
				/>
			)
		case 'textarea':
			return (
				<TextAreaField
					field={fieldProp}
					label={label}
					helperText={descriptionStr}
					isRequired={required}
					size="large"
					error={errorMsg}
				/>
			)
		case 'multi_value_single_select':
			return (
				<SelectField
					field={fieldProp}
					label={label}
					helperText={descriptionStr}
					isRequired={required}
					error={errorMsg}
					size="large"
				>
					<option value={MULTI_SELECT_DEFAULT}>{MULTI_SELECT_DEFAULT}</option>
					{questionField.values.map((value) => (
						<option key={value.value} value={value.value}>
							{value.label}
						</option>
					))}
				</SelectField>
			)
		case 'input_file':
			return (
				<FileInputField
					field={fieldProp}
					label={label}
					helperText={descriptionStr}
					isRequired={required}
					error={errorMsg}
				/>
			)
		default:
			console.error(`Unknown field type: ${questionField.type}`)
			return null
	}
}

const DemographicQuestionField = ({
	question,
}: {
	question: GreenhouseDemographicQuestion
}) => {
	const { register, formState } = useFormContext()
	const { label, required, answer_options } = question
	const questionName = question.id.toString()
	const fieldProp = register(questionName, { required })
	const errorMsg = formState.errors[questionName]?.message?.toString()

	return (
		<RadioGroup.Root
			id={questionName}
			legend={label}
			error={errorMsg}
			isRequired={required}
		>
			{answer_options.map((option) => (
				<RadioGroup.Field
					key={option.id}
					label={option.label}
					field={{
						...fieldProp,
						value: option.id.toString(),
					}}
				/>
			))}
		</RadioGroup.Root>
	)
}

export function DataComplianceField({
	compliance,
}: {
	compliance: DataCompliance[number]
}) {
	const { register, formState } = useFormContext()
	const { requires_consent } = compliance
	const fieldProp = register(COMPLIANCE_FIELD_NAME, {
		required: requires_consent,
	})
	const errorMsg = formState.errors[COMPLIANCE_FIELD_NAME]?.message?.toString()

	return (
		<CheckboxField
			field={fieldProp}
			label={COMPLIANCE_LABEL}
			error={errorMsg}
			isRequired={requires_consent}
		/>
	)
}

interface EducationFieldProps {
	question: EducationQuestions[number]
}

export function EducationQuestionField({ question }: EducationFieldProps) {
	switch (question.id) {
		case 'degree':
			return <ClientSideSearchDropdown question={question} />
		case 'discipline':
			return <ClientSideSearchDropdown question={question} />
		case 'school':
			return <ServerSideSearchDropdown question={question} />
		case 'start_date':
			return <DateRangeInputs question={question} />
		case 'end_date':
			return <DateRangeInputs question={question} />
		default:
			// @ts-expect-error - ts complains this is a never type, which is good because it means we've handled all cases, but we want to be sure so we have this check regardless
			console.error(`Unknown education question: ${question.id}`)
			return null
	}
}
